/**
 * API service for Protocol Assets API
 */

import axios, { AxiosInstance } from 'axios';
import { config } from '../config';
import { oktaAuth } from './oktaAuth';
import {
  Protocol, PRSVersion, PRSContent, ApiResponse,
  ListSessionsResponse as ListSessionsResponseType,
  CreateSessionRequest, CreateSessionResponse as CreateSessionResponseType,
  SessionMetadata,
  ExecutionStatus, WorkflowMode, ExecutionRunningStatus,
} from '../types';

class ApiService {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    // Build default headers
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',  // Triggers CORS preflight
      'Accept': 'application/json',        // Standard accept header
    };

    this.client = axios.create({
      baseURL: config.apiUrl,
      // Default headers sent with EVERY request
      // Note: Browser automatically adds Origin, Access-Control-Request-* headers for CORS
      headers: defaultHeaders,
      timeout: 30000, // 30 seconds
      // CORS credentials configuration
      // false = don't send cookies/credentials (default)
      // true = send cookies/credentials (requires specific CORS config on backend)
      withCredentials: false,
    });

    // Request interceptor - runs before every request
    this.client.interceptors.request.use(
      (config) => {
        // Ensure standard headers are always present
        if (!config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json';
        }
        if (!config.headers['Accept']) {
          config.headers['Accept'] = 'application/json';
        }

        // Add Authorization header if token is available
        if (this.accessToken) {
          config.headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        return config;
      },
      (error) => {
        console.error('[API] Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - runs after every response
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // On 401, try once to renew token and retry the request
        const config = error.config;
        if (
          error.response?.status === 401 &&
          config &&
          !(config as Record<string, unknown>).__tokenRefreshRetried
        ) {
          (config as Record<string, unknown>).__tokenRefreshRetried = true;
          try {
            const newToken = await oktaAuth.tokenManager.renew('accessToken') as { accessToken: string } | undefined;
            if (newToken?.accessToken) {
              this.setAccessToken(newToken.accessToken);
              return this.client.request(config);
            }
          } catch {
            // Renew failed; fall through to normal error handling
          }
        }

        if (error.response) {
          // Server responded with error status
          console.error('[API] Error Response:', {
            status: error.response.status,
            statusText: error.response.statusText,
            url: error.config?.url,
            method: error.config?.method,
            data: error.response.data,
            corsHeaders: {
              'access-control-allow-origin': error.response.headers['access-control-allow-origin'],
            },
          });
        } else if (error.request) {
          // Request made but no response received
          // This is typically a CORS error or network issue
          console.error('[API] No Response (CORS or Network Error):', {
            message: error.message,
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            headers: error.config?.headers,
          });
          
          // Add helpful CORS error message
          if (error.message.includes('Network Error')) {
            console.error(
              '[API] ⚠️  This is likely a CORS error. Check:\n' +
              '  1. Backend CORS configuration allows your origin\n' +
              '  2. Backend returns Access-Control-Allow-Origin header\n' +
              '  3. Run ./test-cors.sh to verify backend CORS setup'
            );
          }
        } else {
          console.error('[API] Error:', error.message);
        }
        throw error;
      }
    );
  }

  /**
   * Set the access token for API requests
   */
  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  /**
   * Get the current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  // ============================================
  // TRIALS API
  // ============================================

  /**
   * Create a new trial in S3
   * Creates folder structure: {trial_id}/metadata.json, app/, web/, prs/, sessions/
   */
  async createTrial(
    trialId: string,
    description?: string
  ): Promise<{success: boolean; trial_id: string; existing?: boolean; message?: string}> {
    try {
      const response = await this.client.post('/trials', {
        trial_id: trialId,
        name: trialId, // name = trial_id as agreed
        description: description || '',
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error creating trial:', error);
      throw error;
    }
  }

  async getTrial(
    trialId: string
  ): Promise<{success: boolean; trial_id: string;
    name: string;
    description: string;
    created_at: string | null;
    app_zip_filename: string | null;
    web_zip_filename: string | null;
    modified_at: string | null;
    platforms: string[] | Array<{platform: string; analysis_available: boolean}>;
    prs_versions: string[] | Array<{version: string; is_base: boolean; uploaded_at?: string}>;
    prs_count: number;
    sessions?: Array<{
      session_id: string;
      name: string;
      platforms: string[];
      created_at: string | null;
      status: string;
      source_prs_version?: string;
      target_prs_version?: string;
    }>;
    sessions_count?: number; message?: string}> {
    try {
      const response = await this.client.get(`/trials/${trialId}`);

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error) {
      console.error('Error getting trial:', error);
      throw error;
    }
  }
  /**
   * List trials with server-side pagination and filtering
   * Returns platforms array, prs_versions array, and prs_count
   */
  async listTrials(params?: {
    limit?: number;
    offset?: number;
    filter?: string;
  }): Promise<{
    success: boolean;
    trials: Array<{
      trial_id: string;
      name: string;
      description: string;
      created_at: string | null;
      app_zip_filename: string | null;
      web_zip_filename: string | null;
      modified_at: string | null;
      platforms: string[] | Array<{platform: string; analysis_available: boolean}>;
      prs_versions: string[] | Array<{version: string; is_base: boolean; uploaded_at?: string}>;
      prs_count: number;
      sessions?: Array<{
        session_id: string;
        name: string;
        platforms: string[];
        created_at: string | null;
        status: string;
        source_prs_version?: string;
        target_prs_version?: string;
      }>;
      sessions_count?: number;
    }>;
    count: number;
    total_count: number;
    limit: number;
    offset: number;
  }> {
    try {
      const response = await this.client.get('/trials', { params });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error listing trials:', error);
      throw error;
    }
  }

  // ============================================
  // PROTOCOLS API
  // ============================================

  /**
   * List all protocols
   */
  async listProtocols(platform: 'app' | 'web' = 'app'): Promise<Protocol[]> {
    try {
      const response = await this.client.get(`/protocols`, {
        params: {platform},
      });

      // Handle nested response structure from Lambda
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body.protocols || [];
    } catch (error) {
      console.error('Error listing protocols:', error);
      throw error;
    }
  }

  /**
   * Get protocol information
   */
  async getProtocol(trialId: string, platform: 'app' | 'web' = 'app'): Promise<any> {
    try {
      const response = await this.client.get(`/protocol/${trialId}`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting protocol:', error);
      throw error;
    }
  }

  /**
   * List PRS versions for a protocol
   */
  async listPRSVersions(trialId: string, platform: 'app' | 'web' = 'app'): Promise<Record<string, PRSVersion>> {
    try {
      const response = await this.client.get(`/protocol/${trialId}/prs`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body.versions || {};
    } catch (error) {
      console.error('Error listing PRS versions:', error);
      throw error;
    }
  }

  /**
   * Get PRS content
   */
  async getPRSContent(
    trialId: string,
    version: string,
    platform: 'app' | 'web' = 'app'
  ): Promise<PRSContent> {
    try {
      const response = await this.client.get(`/protocol/${trialId}/prs/${version}`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body.content || body;
    } catch (error) {
      console.error('Error getting PRS content:', error);
      throw error;
    }
  }

  /**
   * Upload protocol ZIP
   */
  async uploadProtocol(
    trialId: string,
    file: File,
    platform: 'app' | 'web' = 'app'
  ): Promise<ApiResponse> {
    try {
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onload = async () => {
          const base64 = (reader.result as string).split(',')[1];

          try {
            const response = await this.client.post(`/protocol/${trialId}`, {
              zip_content: base64,
              filename: file.name,
            }, {
              params: {platform},
            });

            const body = typeof response.data.body === 'string'
              ? JSON.parse(response.data.body)
              : response.data.body || response.data;

            resolve(body);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('Error uploading protocol:', error);
      throw error;
    }
  }

  /**
   * Upload PRS Excel file
   */
  async uploadPRS(
    trialId: string,
    file: File,
    platform: 'app' | 'web' = 'app',
    isBase: boolean = false
  ): Promise<ApiResponse> {
    try {
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onload = async () => {
          const base64 = (reader.result as string).split(',')[1];

          try {
            const response = await this.client.post(`/protocol/${trialId}/prs`, {
              file_content: base64,
              is_base: isBase,
              filename: file.name,
            }, {
              params: {platform},
            });

            const body = typeof response.data.body === 'string'
              ? JSON.parse(response.data.body)
              : response.data.body || response.data;

            resolve(body);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('Error uploading PRS:', error);
      throw error;
    }
  }

  /**
   * Get cached PRS analysis
   */
  async getCachedAnalysis(
    trialId: string,
    version: string,
    platform: 'app' | 'web' = 'app'
  ): Promise<any> {
    try {
      const response = await this.client.get(`/protocol/${trialId}/prs/${version}/analysis`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting cached analysis:', error);
      return {success: false, cached: false};
    }
  }

  /**
   * Get cached PRS comparison (from sessions API)
   */
  async getCachedComparison(
    trialId: string,
    version1: string,
    version2: string,
    platform: 'app' | 'web' = 'app',
    sessionIdOverride?: string
  ): Promise<any> {
    try {
      const sessionId = sessionIdOverride || `${version1}_to_${version2}`;
      const response = await this.client.get(`/protocol/${trialId}/sessions/${sessionId}/${platform}/comparison`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting cached comparison:', error);
      return {success: false, cached: false};
    }
  }

  /**
   * Get cached modification plan (from sessions API)
   */
  async getCachedPlan(
    trialId: string,
    version1: string,
    version2: string,
    platform: 'app' | 'web' = 'app',
    sessionIdOverride?: string
  ): Promise<any> {
    try {
      const sessionId = sessionIdOverride || `${version1}_to_${version2}`;
      const response = await this.client.get(`/protocol/${trialId}/sessions/${sessionId}/${platform}/plan`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting cached plan:', error);
      return {success: false, cached: false};
    }
  }

  /**
   * Get cached requirements (from sessions API)
   */
  async getCachedRequirements(
    trialId: string,
    version1: string,
    version2: string,
    platform: 'app' | 'web' = 'app',
    sessionIdOverride?: string
  ): Promise<any> {
    try {
      const sessionId = sessionIdOverride || `${version1}_to_${version2}`;
      const response = await this.client.get(`/protocol/${trialId}/sessions/${sessionId}/${platform}/requirements`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting cached requirements:', error);
      return {success: false, cached: false};
    }
  }

  /**
   * Save requirements (to sessions API)
   */
  async saveRequirements(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    requirements: Array<{id: string; content: string}>,
    userEdit: boolean = false
  ): Promise<{success: boolean; message?: string}> {
    try {
      const response = await this.client.post(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/requirements`,
        {
          trial_id: trialId,
          session_id: sessionId,
          platform: platform,
          source: 'manual',
          requirements: requirements,
          user_edit: userEdit,
        }
      );

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return {success: true, ...body};
    } catch (error) {
      console.error('Error saving requirements:', error);
      return {success: false, message: 'Failed to save requirements'};
    }
  }

  /**
   * Get execution summary (from sessions API)
   */
  async getExecutionSummary(
    trialId: string,
    version1: string,
    version2: string,
    platform: 'app' | 'web' = 'app',
    sessionIdOverride?: string
  ): Promise<any> {
    try {
      const sessionId = sessionIdOverride || `${version1}_to_${version2}`;
      const response = await this.client.get(`/protocol/${trialId}/sessions/${sessionId}/${platform}/execution-summary`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting execution summary:', error);
      return {success: false, cached: false};
    }
  }

  async getExecutionRunningStatus(
    trialId: string,
    version1: string,
    version2: string,
    platform: 'app' | 'web' = 'app',
    sessionIdOverride?: string,
    execution_id: string = 'exec_001',
  ): Promise<ExecutionRunningStatus> {
    try {
      const sessionId = sessionIdOverride || `${version1}_to_${version2}`;
      const response = await this.client.get(`/protocol/${trialId}/sessions/${sessionId}/${platform}/executions/${execution_id}/status`, {
        params: {platform},
      });

      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      return body;
    } catch (error) {
      console.error('Error getting execution summary:', error);
      return {success: false, status: 'error', message: 'Failed to get execution running status'};
    }
  }

  /**
   * Download generated protocol as ZIP
   * Uses native fetch for better binary data handling
   */
  async downloadProtocol(
    trialId: string,
    version1: string,
    version2: string,
    platform: 'app' | 'web' = 'app',
    sessionIdOverride?: string
  ): Promise<Blob> {
    try {
      const sessionId = sessionIdOverride || `${version1}_to_${version2}`;
      const url = `${this.client.defaults.baseURL}/protocol/${trialId}/sessions/${sessionId}/${platform}/download?platform=${platform}`;

      // Build headers with Authorization if token is available
      const headers: Record<string, string> = {
        'Accept': 'application/zip',
      };
      
      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
      }

      // Use native fetch for binary data (more reliable than Axios)
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Get as blob directly (don't use .json())
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error downloading protocol:', error);
      throw error;
    }
  }

  // ============================================
  // SESSIONS API
  // ============================================

  /**
   * List sessions for a trial
   */
  async listSessions(trialId: string): Promise<ListSessionsResponseType> {
    try {
      const response = await this.client.get(`/protocol/${trialId}/sessions`);
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return { success: true, sessions: [], count: 0 };
      }
      console.error('Error listing sessions:', error);
      throw error;
    }
  }

  /**
   * Create a new session from PRS versions
   */
  async createSession(request: CreateSessionRequest): Promise<CreateSessionResponseType> {
    try {
      const response = await this.client.post(
        `/protocol/${request.trial_id}/sessions`,
        request
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  /**
   * Check if a session exists
   */
  async sessionExists(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<boolean> {
    try {
      await this.client.get(`/protocol/${trialId}/sessions/${sessionId}/${platform}`);
      return true;
    } catch (error: any) {
      if (error?.response?.status === 404) return false;
      throw error;
    }
  }

  /**
   * Get session metadata by looking up from sessions list
   */
  async getSessionMetadata(
    trialId: string,
    sessionId: string
  ): Promise<SessionMetadata | null> {
    try {
      const response = await this.listSessions(trialId);
      const session = response.sessions.find((s) => s.session_id === sessionId);
      if (!session) return null;
      return {
        session_id: session.session_id,
        name: session.name,
        source_prs_version: session.source_prs_version,
        target_prs_version: session.target_prs_version,
      };
    } catch (error) {
      console.error('Error getting session metadata:', error);
      throw error;
    }
  }

  // ============================================
  // WORKFLOW MODE API
  // ============================================

  /**
   * Get workflow mode for a session
   *
   * Determines if Wizard/Copilot can be used.
   */
  async getWorkflowMode(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<WorkflowMode> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/workflow-mode`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;

      // Map API response fields to our interface
      // API returns: wizard_allowed, reason
      // We expect: can_use_wizard, can_clear_plans, wizard_disabled_reason
      return {
        mode: body.mode || 'fresh',
        can_use_wizard: body.can_use_wizard ?? body.wizard_allowed ?? false,
        can_use_copilot: body.can_use_copilot ?? false,
        can_clear_plans: body.can_clear_plans ?? false,
        wizard_disabled_reason: body.wizard_disabled_reason || body.reason,
        requirements_summary: body.requirements_summary,
      };
    } catch (error) {
      console.error('Error getting workflow mode:', error);
      throw error;
    }
  }

  // ============================================
  // SESSION DATA API (session-based accessors)
  // ============================================

  /**
   * Get cached requirements for a session (by sessionId directly)
   */
  async getSessionRequirements(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<any> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/requirements`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting session requirements:', error);
      throw error;
    }
  }

  /**
   * Get cached plan for a session (by sessionId directly)
   */
  async getSessionPlan(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<any> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/plan`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting session plan:', error);
      throw error;
    }
  }

  /**
   * Save surgical plan actions
   */
  async saveSurgicalPlan(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    actions: any[],
    userEdit: boolean = false
  ): Promise<void> {
    try {
      await this.client.post(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/plan/actions`,
        { 
          trial_id: trialId,
          session_id: sessionId,
          platform: platform,
          actions: actions,
          user_edit: userEdit,
        }
      );
    } catch (error) {
      console.error('Error saving surgical plan:', error);
      throw error;
    }
  }

  /**
   * Get PRS comparison data for a session
   */
  async getSessionComparison(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<any> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/comparison`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting session comparison:', error);
      throw error;
    }
  }

  /**
   * Get execution summary for a session (by sessionId directly)
   */
  async getSessionExecutionSummary(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<any> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/execution-summary`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting session execution summary:', error);
      throw error;
    }
  }

  /**
   * Download generated protocol as ZIP (by sessionId directly)
   */
  async downloadProtocolBySession(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<Blob> {
    try {
      const url = `${this.client.defaults.baseURL}/protocol/${trialId}/sessions/${sessionId}/${platform}/download?platform=${platform}`;
      const headers: Record<string, string> = { 'Accept': 'application/zip' };
      if (this.accessToken) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
      }
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      return await response.blob();
    } catch (error) {
      console.error('Error downloading protocol:', error);
      throw error;
    }
  }

  // ============================================
  // EXECUTION LIFECYCLE API
  // ============================================

  /**
   * Get workflow state for a session
   */
  async getWorkflowState(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<any | null> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/state`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting workflow state:', error);
      throw error;
    }
  }

  /**
   * Get current execution status for a session
   *
   * Fetches workflow state first to find execution ID, then gets full execution details.
   */
  async getCurrentExecution(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<ExecutionStatus | null> {
    try {
      const state = await this.getWorkflowState(trialId, sessionId, platform);
      if (!state) return null;

      const currentExecution = state.current_execution;
      if (!currentExecution?.execution_id) return null;

      return await this.getExecution(
        trialId, sessionId, platform, currentExecution.execution_id
      );
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting current execution:', error);
      throw error;
    }
  }

  /**
   * Get execution details by ID
   */
  async getExecution(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    executionId: string
  ): Promise<ExecutionStatus | null> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/executions/${executionId}`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      console.error('Error getting execution:', error);
      throw error;
    }
  }

  /**
   * Commit an execution - apply staged files to protocol
   */
  async commitExecution(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    executionId: string
  ): Promise<void> {
    try {
      await this.client.post(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/executions/${executionId}/commit`
      );
    } catch (error) {
      console.error('Error committing execution:', error);
      throw error;
    }
  }

  /**
   * Discard an execution - delete staged files
   */
  async discardExecution(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    executionId: string
  ): Promise<void> {
    try {
      await this.client.post(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/executions/${executionId}/discard`
      );
    } catch (error) {
      console.error('Error discarding execution:', error);
      throw error;
    }
  }

  // ============================================
  // CLEAR ALL PLANS API
  // ============================================

  /**
   * Clear all plans for all planned requirements
   *
   * Deletes all actions from surgical_plan.json and resets
   * all planned requirements to pending.
   */
  async clearAllPlans(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web'
  ): Promise<{ success: boolean; requirements_cleared: number; actions_deleted: number }> {
    try {
      const response = await this.client.post(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/clear-all-plans`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error) {
      console.error('Error clearing all plans:', error);
      throw error;
    }
  }

  // ============================================
  // SESSION INSTRUCTIONS API
  // ============================================

  /**
   * Get step-specific instructions for a session
   * @param step - One of: 'requirements', 'plan', 'execution'
   */
  async getSessionInstructions(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    step: 'requirements' | 'plan' | 'execution'
  ): Promise<{
    success: boolean;
    instructions?: string;
    created_at?: string;
    modified_at?: string;
  } | null> {
    try {
      const response = await this.client.get(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/instructions/${step}`
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error: any) {
      if (error?.response?.status === 404) {
        // No instructions found - return null instead of throwing
        return null;
      }
      console.error('Error getting session instructions:', error);
      throw error;
    }
  }

  /**
   * Save step-specific instructions for a session
   * @param step - One of: 'requirements', 'plan', 'execution'
   */
  async saveSessionInstructions(
    trialId: string,
    sessionId: string,
    platform: 'app' | 'web',
    step: 'requirements' | 'plan' | 'execution',
    instructions: string
  ): Promise<{ success: boolean; saved_at?: string; message?: string }> {
    try {
      const response = await this.client.post(
        `/protocol/${trialId}/sessions/${sessionId}/${platform}/instructions/${step}`,
        {
          trial_id: trialId,
          session_id: sessionId,
          platform: platform,
          step: step,
          instructions: instructions,
        }
      );
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return { success: true, ...body };
    } catch (error) {
      console.error('Error saving session instructions:', error);
      return { success: false, message: 'Failed to save instructions' };
    }
  }

  // ============================================
  // ENHANCED TRIAL CREATION
  // ============================================

  /**
   * Create a new trial with type and supported platforms
   */
  async createTrialV2(
    trialId: string,
    options?: {
      description?: string;
      type?: 'change_in_scope' | 'new_protocol';
      supportedPlatforms?: ('app' | 'web')[];
    }
  ): Promise<{ success: boolean; trial_id: string; existing?: boolean; message?: string }> {
    try {
      const response = await this.client.post('/trials', {
        trial_id: trialId,
        name: trialId,
        description: options?.description || '',
        type: options?.type || 'change_in_scope',
        supported_platforms: options?.supportedPlatforms || ['app', 'web'],
      });
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return body;
    } catch (error) {
      console.error('Error creating trial:', error);
      throw error;
    }
  }

  /**
   * Update trial description
   */
  async updateTrialDescription(
    trialId: string,
    description: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await this.client.post(`/trials/${trialId}/description`, {
        description: description,
      });
      const body = typeof response.data.body === 'string'
        ? JSON.parse(response.data.body)
        : response.data.body || response.data;
      return { success: true, ...body };
    } catch (error) {
      console.error('Error updating trial description:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
